import * as yup from 'yup';
import { generateUUID } from '@utils/utils.tsx';
import { ExternalOrderRequest, ExternalOrderResponse, GetExternalOrdersResponse, GlobalOrderItemDetailsRequest, PriceRequest } from '@api/ringil-api.v2.tsx';

export const NO_ORDERS_NOW_ORDER_NUMBER = 'NO_ORDERS_NOW';

// The PAP picker is linkable — and `externalOrders` may be sent — only when the integration AND this location are both
// mapped upstream. If either flag is false we must not surface the picker nor send external orders. Shared by SN + timeslots.
export const isExternalIdPresent = (response?: GetExternalOrdersResponse): boolean =>
  response?.externalIdPresent === true && response?.externalIdLocationPresent === true;

// The upstream PAP picker returns a single pinned NO_ORDERS_NOW sentinel when there are no real orders
// for the chosen carrier + location pair. Detect that case so callers can warn and block (e.g. SN create).
export const hasOnlyNoOrdersNow = (orders?: ExternalOrderResponse[]): boolean =>
  Array.isArray(orders) && orders.length > 0 && orders.every(o => o.orderNumber === NO_ORDERS_NOW_ORDER_NUMBER);

export interface Order {
  id?: string;
  orderName: string;
  items: OrderItem[];
  orderNote?: string;
  orderValue?: PriceRequest;
  orderAmount?: number;
  orderDescription?: string;
  unloadingLocationId?: string;
  orderTotalWeight?: number;
  orderDiscrepancyNote?: string;
  eanCodes?: string[];
  externalOrders?: ExternalOrderRequest[];
}

export interface OrderItem {
  id?: string;
  amount: number;
  cargoTemplateCode: string;
  decimalPlaces: number;
  stackable: boolean;
  itemNote?: string;
  height?: number;
  width?: number;
  length?: number;
  weight?: number;
  discrepancyAmount?: number;
  details?: GlobalOrderItemDetailsRequest[];
}

export const emptyOrder = (addItem: boolean, generateOrderId: boolean = false): Order => {
  if (generateOrderId) {
    return {
      id: generateUUID(),
      orderName: '',
      items: addItem ? generateOrderItem() : [],
      orderAmount: null,
      orderDescription: null,
    };
  } else {
    return {
      orderName: '',
      items: addItem ? generateOrderItem() : [],
      orderAmount: null,
      orderDescription: null,
    };
  }
};

export const generateOrderItem = () => {
  return [{ amount: null, cargoTemplateCode: null } as OrderItem];
};

export const calculateOrderVolume = (items: OrderItem[]): number =>
  (items || []).reduce(
    (acc, i) => acc + ((i.height || 0) * (i.width || 0) * (i.length || 0) * (i.amount || 0)) / 1_000_000,
    0,
  );

export const orderSchema = (externalIdPresent: boolean = false) =>
  yup.array().of(
    yup.object().shape({
      orderName: externalIdPresent ? yup.string().nullable() : yup.string().required().min(1),
      externalOrders: externalIdPresent
        ? yup
          .array()
          .of(
            yup.object().shape({
              orderNumber: yup.string().required(),
            }),
          )
          .min(1, 'externalOrdersRequired')
          .required('externalOrdersRequired')
          .test('externalOrdersNoOrdersNowExclusive', 'externalOrdersNoOrdersNowExclusive', value => {
            if (!value || value.length < 2) return true;
            const list = value as ExternalOrderRequest[];
            return !list.some(v => v.orderNumber === NO_ORDERS_NOW_ORDER_NUMBER);
          })
          .test('externalOrdersIntersect', 'externalOrdersNoIntersection', value => {
            if (!value || value.length < 2) return true;
            const toMs = (s?: string) => {
              if (!s) return null;
              const t = new Date(s).getTime();
              return Number.isNaN(t) ? null : t;
            };
            const list = value as ExternalOrderRequest[];
            const starts = list.map(v => toMs(v.timeFrom)).filter((n): n is number => n !== null);
            const ends = list.map(v => toMs(v.timeTo)).filter((n): n is number => n !== null);
            if (starts.length === 0 || ends.length === 0) return true;
            return Math.max(...starts) <= Math.min(...ends);
          })
        : yup.array().nullable(),
      items: yup.array().of(
        yup.object().shape({
          cargoTemplateCode: yup.string().required(),
          amount: yup.number().required(),
        }),
      ),
    }),
  );
